import { Link } from "react-router-dom";
import {
    MessageCircle, ArrowUpRight, Satellite, Wrench, Headphones,
    Store, Shield, MapPin, Star, Zap, BadgeCheck
} from "lucide-react";
import { openWhatsApp } from "../lib/whatsapp";
import { useT } from "../lib/i18n";
import { ComingSoonSection } from "../components/site/ComingSoonSection";
import { Testimonials } from "../components/site/Testimonials";

export default function Home() {
    const t = useT();

    const services = [
        { icon: Satellite, title: t("New DTH Connections", "కొత్త DTH కనెక్షన్లు"), desc: t("Fast, professional Sun Direct installations across Armoor and 40 km radius.", "ఆర్మూర్ మరియు 40 కిమీ పరిధిలో వేగవంతమైన, ప్రొఫెషనల్ సన్ డైరెక్ట్ ఇన్‌స్టాలేషన్‌లు.") },
        { icon: Zap, title: t("Recharges & Renewals", "రీఛార్జ్‌లు & రెన్యూవల్‌లు"), desc: t("Walk-in or WhatsApp recharge — every Sun Direct pack and add-on supported.", "షాపుకు రండి లేదా వాట్సాప్‌లో రీఛార్జ్ చేయండి — అన్ని సన్ డైరెక్ట్ ప్యాక్‌లు మద్దతిస్తాయి.") },
        { icon: Wrench, title: t("Setup & Service", "సెటప్ & సర్వీస్"), desc: t("Antenna alignment, set-top box repair and complete on-site troubleshooting.", "యాంటెన్నా అలైన్‌మెంట్, సెట్-టాప్ బాక్స్ రిపేర్ మరియు పూర్తి ఆన్-సైట్ ట్రబుల్‌షూటింగ్.") },
        { icon: Headphones, title: t("Customer Support", "కస్టమర్ సపోర్ట్"), desc: t("Local team that picks up the phone — in Telugu, Hindi and English.", "తెలుగు, హిందీ మరియు ఇంగ్లీష్‌లో ఫోన్ ఎత్తే లోకల్ టీమ్.") },
    ];

    const trust = [
        { icon: BadgeCheck, label: t("Authorized Distributor", "అధికారిక డిస్ట్రిబ్యూటర్"), value: t("Sun Direct", "సన్ డైరెక్ట్") },
        { icon: MapPin, label: t("Operational Radius", "సేవా పరిధి"), value: t("40 km", "40 కిమీ") },
        { icon: Star, label: t("Trusted Households", "నమ్మిన గృహాలు"), value: "1000+" },
        { icon: Shield, label: t("Years Serving Armoor", "ఆర్మూర్‌లో సేవ"), value: t("Local Expertise", "స్థానిక నైపుణ్యం") },
    ];

    const towns = ["Armoor", "Balkonda", "Bheemgal", "Velpur", "Nandipet", "Mortad", "Kammarpally", "Mendora", "Perkit", "Yedapally", "Jakranpally", "Pitlam"];

    return (
        <div data-testid="page-home">
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-32">
                    <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
                        <div className="md:col-span-7 fade-up">
                            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-rvs-navy-10">
                                <span className="w-2 h-2 rounded-full bg-rvs-orange animate-pulse"></span>
                                <span className="text-xs font-semibold tracking-[0.18em] uppercase text-rvs-navy">
                                    {t("Authorized Sun Direct Distributor", "అధికారిక సన్ డైరెక్ట్ డిస్ట్రిబ్యూటర్")}
                                </span>
                            </div>
                            <h1 style={{fontFamily: "'Outfit', 'Noto Sans Telugu', sans-serif"}} className="mt-7 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02] text-rvs-navy">
                                {t("Sun Direct,", "సన్ డైరెక్ట్,")}<br />
                                <span className="text-rvs-orange">{t("delivered locally", "స్థానికంగా అందిస్తున్నాం")}</span>
                                <br />{t("in Armoor.", "ఆర్మూర్‌లో.")}
                            </h1>
                            <p className="mt-7 text-lg md:text-xl text-[#4A5568] max-w-xl leading-relaxed">
                                {t(
                                    "RVS Electronics is the trusted Sun Direct distributor for Armoor and surrounding 40 km — connections, recharges, installations, and a retailer network you can grow with.",
                                    "ఆర్మూర్ మరియు చుట్టుపక్కల 40 కిమీ పరిధిలో నమ్మదగిన సన్ డైరెక్ట్ డిస్ట్రిబ్యూటర్ — RVS ఎలక్ట్రానిక్స్. కనెక్షన్‌లు, రీఛార్జ్‌లు, ఇన్‌స్టాలేషన్‌లు మరియు మీరు అభివృద్ధి చెందగల రిటైలర్ నెట్‌వర్క్."
                                )}
                            </p>

                            <div className="mt-9 flex flex-wrap gap-3">
                                <button
                                    onClick={() => openWhatsApp(t("Hello RVS Electronics, I'd like a new Sun Direct connection.", "హలో RVS ఎలక్ట్రానిక్స్, నాకు కొత్త సన్ డైరెక్ట్ కనెక్షన్ కావాలి."))}
                                    data-testid="hero-whatsapp-btn"
                                    className="btn-orange inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold"
                                >
                                    <MessageCircle className="w-4 h-4" /> {t("WhatsApp Us", "వాట్సాప్‌లో సంప్రదించండి")} <ArrowUpRight className="w-4 h-4" />
                                </button>
                                <Link
                                    to="/services"
                                    data-testid="hero-services-link"
                                    className="btn-ghost-navy inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold"
                                >
                                    {t("Explore Services", "సేవలను చూడండి")}
                                </Link>
                            </div>

                            <div className="mt-10 flex items-center gap-5 text-sm text-[#4A5568]">
                                <div className="flex -space-x-2">
                                    {[1,2,3,4].map(i => (
                                        <div key={i} className="w-9 h-9 rounded-full border-2 border-[#F9F6F0] flex items-center justify-center text-white text-xs font-bold"
                                             style={{background: ["#FF5A00","#0B192C","#FFB200","#1E2A3B"][i-1]}}>
                                            {["R","V","S","E"][i-1]}
                                        </div>
                                    ))}
                                </div>
                                <p><span className="font-bold text-rvs-navy">{t("1000+ households", "1000+ గృహాలు")}</span> {t("served across Nizamabad district.", "నిజామాబాద్ జిల్లా అంతటా సేవ.")}</p>
                            </div>
                        </div>

                        <div className="md:col-span-5">
                            <div className="relative">
                                <div className="absolute -inset-6 bg-rvs-orange/10 rounded-[28px] blur-2xl" aria-hidden></div>
                                <div className="relative rounded-[24px] overflow-hidden border border-rvs-navy-10 bg-white">
                                    <img
                                        src="https://static.prod-images.emergentagent.com/jobs/9cd8c857-d34b-45e2-870a-c741d4772794/images/ee6a15c386a5f0d884ee36f5f0ce5a0793360b073a5db972cce0e758f533f51c.png"
                                        alt="Sun Direct DTH dish on a clean modern home"
                                        className="w-full h-[420px] md:h-[520px] object-cover"
                                    />
                                </div>
                                <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl border border-rvs-navy-10 p-4 shadow-xl flex items-center gap-3 max-w-[240px]">
                                    <span className="w-10 h-10 rounded-xl bg-rvs-orange/15 flex items-center justify-center">
                                        <BadgeCheck className="w-5 h-5 text-rvs-orange" />
                                    </span>
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wider text-rvs-orange">{t("Verified", "ధృవీకరించబడింది")}</p>
                                        <p className="text-sm font-semibold text-rvs-navy leading-tight">{t("Authorized Sun Direct partner", "అధికారిక సన్ డైరెక్ట్ భాగస్వామి")}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* TRUST ROW */}
            <section className="border-y border-rvs-navy-10 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
                    {trust.map((tr) => {
                        const Icon = tr.icon;
                        return (
                            <div key={tr.label} className="flex items-center gap-4">
                                <span className="w-11 h-11 rounded-xl bg-rvs-orange/10 flex items-center justify-center shrink-0">
                                    <Icon className="w-5 h-5 text-rvs-orange" />
                                </span>
                                <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-[#4A5568] font-semibold">{tr.label}</p>
                                    <p style={{fontFamily: "'Outfit', 'Noto Sans Telugu', sans-serif"}} className="text-lg md:text-xl font-bold text-rvs-navy">{tr.value}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* SERVICES */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
                <div className="grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-7">
                        <p className="eyebrow">{t("What we do", "మేము ఏం చేస్తాము")}</p>
                        <h2 style={{fontFamily: "'Outfit', 'Noto Sans Telugu', sans-serif"}} className="mt-4 text-4xl md:text-5xl font-black tracking-tight text-rvs-navy leading-[1.05]">
                            {t("End-to-end Sun Direct services for homes and shops.", "ఇళ్లకు మరియు షాపులకు సంపూర్ణ సన్ డైరెక్ట్ సేవలు.")}
                        </h2>
                    </div>
                    <div className="md:col-span-5">
                        <p className="text-base md:text-lg text-[#4A5568] leading-relaxed">
                            {t("From the first installation to the next year's recharge, we handle every step so you never have to chase a call centre.", "మొదటి ఇన్‌స్టాలేషన్ నుండి తదుపరి సంవత్సరం రీఛార్జ్ వరకు, మేము ప్రతి దశను చూసుకుంటాము — మీరు కాల్ సెంటర్ వెంట పడాల్సిన పని లేదు.")}
                        </p>
                    </div>
                </div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {services.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <div key={i} className="card-default p-7 fade-up" style={{animationDelay: `${i*80}ms`}} data-testid={`service-card-${i}`}>
                                <span className="w-12 h-12 rounded-xl bg-rvs-orange/10 flex items-center justify-center">
                                    <Icon className="w-6 h-6 text-rvs-orange" />
                                </span>
                                <h3 style={{fontFamily: "'Outfit', 'Noto Sans Telugu', sans-serif"}} className="mt-6 text-xl font-bold text-rvs-navy">{s.title}</h3>
                                <p className="mt-3 text-sm text-[#4A5568] leading-relaxed">{s.desc}</p>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-12">
                    <Link to="/services" data-testid="services-explore-link" className="inline-flex items-center gap-2 text-sm font-semibold text-rvs-navy link-underline">
                        {t("See all services", "అన్ని సేవలను చూడండి")} <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            <Testimonials />

            {/* RETAILER CTA */}
            <section className="bg-[#FFE5D9]/40">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7">
                        <p className="eyebrow">{t("For retailers", "రిటైలర్ల కోసం")}</p>
                        <h2 style={{fontFamily: "'Outfit', 'Noto Sans Telugu', sans-serif"}} className="mt-4 text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter text-rvs-navy leading-[1.05]">
                            {t("Build a Sun Direct counter in your shop — we'll handle the rest.", "మీ షాపులో సన్ డైరెక్ట్ కౌంటర్ ఏర్పాటు చేయండి — మిగతావి మేము చూసుకుంటాము.")}
                        </h2>
                        <p className="mt-6 text-base md:text-lg text-[#4A5568] max-w-2xl leading-relaxed">
                            {t("We onboard kirana stores, mobile shops and electronics dealers across Armoor with stock, support, and earnings on every recharge.", "ఆర్మూర్ అంతటా కిరాణా షాపులు, మొబైల్ షాపులు మరియు ఎలక్ట్రానిక్స్ డీలర్‌లను స్టాక్, సపోర్ట్ మరియు ప్రతి రీఛార్జ్‌పై ఆదాయంతో చేరుస్తాము.")}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/retailers" data-testid="home-retailers-link" className="btn-ghost-navy inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold">
                                <Store className="w-4 h-4" /> {t("Become a Retailer", "రిటైలర్‌గా చేరండి")}
                            </Link>
                            <button
                                onClick={() => openWhatsApp(t("Hi RVS Electronics, I'm a retailer and want to partner with you.", "హాయ్ RVS ఎలక్ట్రానిక్స్, నేను రిటైలర్‌ను, మీతో భాగస్వామిగా చేరాలనుకుంటున్నాను."))}
                                data-testid="home-retailer-whatsapp-btn"
                                className="btn-orange inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold"
                            >
                                <MessageCircle className="w-4 h-4" /> {t("Talk to Us", "మాతో మాట్లాడండి")}
                            </button>
                        </div>
                    </div>
                    <div className="md:col-span-5">
                        <img
                            src="https://static.prod-images.emergentagent.com/jobs/9cd8c857-d34b-45e2-870a-c741d4772794/images/cb99cbbb32cd4024c1de81c199c8b55414185883db2825baef0ae4357d2c6020.png"
                            alt="Retailer at electronics shop counter"
                            className="rounded-[20px] w-full h-[400px] object-cover border border-rvs-navy-10"
                        />
                    </div>
                </div>
            </section>

            {/* COVERAGE MARQUEE */}
            <section className="bg-rvs-navy text-white py-10 overflow-hidden" data-testid="coverage-marquee">
                <div className="marquee whitespace-nowrap">
                    {[...towns, ...towns].map((tw, i) => (
                        <span key={i} className="inline-flex items-center gap-4 px-8 text-sm md:text-base uppercase tracking-[0.3em] text-white/70 font-semibold">
                            <MapPin className="w-4 h-4 text-rvs-orange" /> {tw}
                            <span className="text-rvs-orange">•</span>
                        </span>
                    ))}
                </div>
            </section>

            <ComingSoonSection />
        </div>
    );
}
